// AI assisted development
/**
 * DevAgent API Client
 * 
 * Provides methods to interact with the DevAgent backend API
 */

import type {
  PipelineDetail,
  CreatePipelineRequest,
  CreatePipelineResponse,
  ListPipelinesResponse,
  DashboardStats,
  ApproveStageRequest,
  ApproveStageResponse,
  AssignDeveloperRequest,
  AssignDeveloperResponse,
  ResolveRequirementGapRequest,
  ResolveRequirementGapResponse,
  StartImplementationResponse,
  StartImplementationRequest,
  ConfirmManualImplementationResponse,
  RetryPipelineResponse,
  CancelPipelineResponse,
  PipelineListFilters,
  PipelineTestStatusResponse,
  TestLogEntry,
  DefectInvestigationResponse,
  ReviewDecisionStatus,
  DocumentContentResponse,
  DocumentListResponse,
  DocumentQualityValidationStatus,
  DocumentSetResponse,
  ApproveDocumentPackageRequest,
  ApproveDocumentPackageResponse,
  RemoveRepositoryRequest,
  RemoveRepositoryResponse,
  ChangeRepositoriesRequest,
  ChangeRepositoriesResponse,
  DevAgentCapabilitiesResponse,
  PipelineFacets,
  AnalyticsDateRange,
  AnalyticsTimeRange,
  AssignStageReviewerPayload,
  AssignStageReviewerResponse,
  ReassignOwnershipPayload,
  ReassignOwnershipResponse,
  UserSearchResponse,
  RepositoryWorkspaceResponse,
  RepoSelectionAccuracyResponse,
  RepoOverSelectionResponse,
  DocumentEditStatus,
  DocumentEditReservation,
  BranchPreparationStatus,
} from '../types/devagent';

// DevAgent API access mode:
// - Production: Proxied through Azure Functions backend at /api/devagent/* (API key stays server-side)
// - Development: Direct connection to DevAgent (localhost:8080) with API key in env var
//
// SECURITY: The API key is NEVER sent to the browser in production.
// The Azure Functions proxy injects it server-side.

import { getAccessToken, isAuthenticated } from './auth';
import { userFacingError } from '../utils/userFacingError';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

// Direct DevAgent URL - only used in local development when VITE_DEVAGENT_API_URL is set
const DIRECT_DEVAGENT_URL = import.meta.env.VITE_DEVAGENT_API_URL || '';
const DIRECT_API_KEY = import.meta.env.VITE_DEVAGENT_API_KEY || '';

// Use proxy when no direct URL is configured; use direct mode when VITE_DEVAGENT_API_URL is set
// (works for both local Docker builds and vite dev server)
const USE_PROXY = !DIRECT_DEVAGENT_URL;
const DEVAGENT_READ_TIMEOUT_MS = 35_000;
const PIPELINE_CREATE_TIMEOUT_MS = 165_000;

function getDashboardBaseUrl(): string {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }
  return window.location.origin.replace(/\/$/, '');
}

type DevAgentErrorPayload = {
  error?: unknown;
  message?: unknown;
  detail?: unknown;
};

export class DevAgentApiError extends Error {
  status: number;
  data?: unknown;
  response?: { status: number; data?: unknown };

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'DevAgentApiError';
    this.status = status;
    this.data = data;
    this.response = {
      status,
      data,
    };
  }
}

function extractErrorMessage(errorData: unknown, fallbackMessage: string): string {
  if (!errorData) {
    return fallbackMessage;
  }

  if (typeof errorData === 'string') {
    const trimmed = errorData.trim();
    return trimmed || fallbackMessage;
  }

  if (typeof errorData !== 'object') {
    return fallbackMessage;
  }

  const payload = errorData as DevAgentErrorPayload;
  const directMessage = [payload.message, payload.error].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );
  if (directMessage) {
    return directMessage;
  }

  if (payload.detail !== undefined) {
    return extractErrorMessage(payload.detail, fallbackMessage);
  }

  return fallbackMessage;
}

async function readErrorPayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get('Content-Type') || '';

  if (contentType.includes('application/json')) {
    return response.json().catch(() => undefined);
  }

  const bodyText = await response.text().catch(() => '');
  return bodyText ? { message: bodyText } : undefined;
}

function buildApiError(errorData: unknown, status: number): DevAgentApiError {
  const fallbackMessage = status > 0 ? `HTTP ${status}` : 'DevAgent request failed';
  return new DevAgentApiError(
    userFacingError(extractErrorMessage(errorData, fallbackMessage), status),
    status,
    errorData
  );
}

function normalizeNetworkError(error: unknown): DevAgentApiError {
  if (error instanceof DevAgentApiError) {
    return error;
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return new DevAgentApiError(
      'The DevAgent request was cancelled before it completed.',
      0,
      { error: error.message }
    );
  }

  if (error instanceof Error && error.message === 'Failed to fetch') {
    return new DevAgentApiError(
      'Unable to complete the DevAgent request. Azure or DevAgent may have timed out before returning a response.',
      0,
      { error: error.message }
    );
  }

  if (error instanceof Error) {
    return new DevAgentApiError(userFacingError(error), 0, { error: error.message });
  }

  return new DevAgentApiError('DevAgent request failed', 0);
}

/**
 * Base fetch wrapper with error handling.
 * 
 * In production: Routes through Azure Functions proxy (/api/devagent/*)
 *   - Uses Azure AD Bearer token for auth (same as all other API calls)
 *   - API key is injected server-side by the proxy
 * 
 * In development: Can call DevAgent directly if VITE_DEVAGENT_API_URL is set
 *   - Uses X-Api-Key header with VITE_DEVAGENT_API_KEY
 */
async function devagentFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  rawResponse = false,
): Promise<T> {
  let url: string;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const dashboardBaseUrl = getDashboardBaseUrl();
  if (dashboardBaseUrl) {
    headers['X-Dashboard-Base-Url'] = dashboardBaseUrl;
  }
  
  if (USE_PROXY) {
    // Production: Proxy through Azure Functions (API key stays server-side)
    // Strip leading slash from endpoint for clean URL construction
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    url = `${API_BASE_URL}/devagent/${cleanEndpoint}`;
    
    // Use Azure AD Bearer token (same auth as all other dashboard API calls)
    if (isAuthenticated()) {
      try {
        const token = await getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        console.warn('[DevAgent API] Failed to get access token:', e);
      }
    }
  } else {
    // Local development: Direct connection to DevAgent
    url = `${DIRECT_DEVAGENT_URL}${endpoint}`;
    
    if (DIRECT_API_KEY) {
      headers['X-Api-Key'] = DIRECT_API_KEY;
    } else {
      console.warn('[DevAgent API] No API key set for direct DevAgent connection');
    }
  }
  
  // Add any additional headers from options
  if (options.headers) {
    const optHeaders = options.headers instanceof Headers 
      ? Object.fromEntries(options.headers.entries())
      : options.headers as Record<string, string>;
    Object.assign(headers, optHeaders);
  }

  const requestMethod = (options.method || 'GET').toUpperCase();
  const timeoutMs = requestMethod === 'GET' ? DEVAGENT_READ_TIMEOUT_MS : 360_000;
  const timeoutController = new AbortController();
  let requestTimedOut = false;
  const timeoutId = globalThis.setTimeout(() => {
    requestTimedOut = true;
    timeoutController.abort();
  }, timeoutMs);
  const callerSignal = options.signal;
  const abortFromCaller = () => timeoutController.abort();
  if (callerSignal?.aborted) {
    timeoutController.abort();
  } else {
    callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      signal: timeoutController.signal,
    });
  } catch (error) {
    if (requestTimedOut) {
      throw new DevAgentApiError(
        `DevAgent did not respond within ${Math.round(timeoutMs / 1000)} seconds.`,
        504,
        { error: 'client_read_timeout', timeout_ms: timeoutMs },
      );
    }
    throw normalizeNetworkError(error);
  } finally {
    globalThis.clearTimeout(timeoutId);
    callerSignal?.removeEventListener('abort', abortFromCaller);
  }

  if (!response.ok) {
    const errorData = await readErrorPayload(response);
    throw buildApiError(errorData, response.status);
  }
  if (rawResponse) return response as T;

  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text() as Promise<T>;
}

export interface FeedbackAttachmentExtraction {
  filename: string;
  sha256: string;
  text: string;
  limitations: string[];
  text_only?: boolean;
}

/** Extract a user-provided document without changing sealed pipeline evidence. */
export function extractFeedbackAttachment(
  pipelineId: string,
  filename: string,
  contentBase64: string,
  signal?: AbortSignal,
): Promise<FeedbackAttachmentExtraction> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/feedback-attachments/extract`, {
    method: 'POST', signal,
    body: JSON.stringify({ filename, content_base64: contentBase64 }),
  });
}

// Map frontend status names to backend status values
const STATUS_MAP: Record<string, string> = {
  'running': 'in_progress',
  'completed': 'completed',
  'failed': 'failed',
  'cancelled': 'cancelled',
  'waiting_approval': 'waiting_approval',
  // Allow direct database values too
  'in_progress': 'in_progress',
};

// ---------------------------------------------------------------------------
// T020 – ETag cache: maps filter-hash → last ETag received from the server
// ---------------------------------------------------------------------------
const _etagCache = new Map<string, string>();
const _listResponseCache = new Map<string, ListPipelinesResponse>();

/** Compute a stable cache key from filter params for ETag tracking. */
function _filterHash(filters?: PipelineListFilters): string {
  if (!filters) return '__default__';
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(filters)
        .filter(([, v]) => v !== undefined && v !== null)
        .sort(([a], [b]) => a.localeCompare(b))
    )
  );
}

/**
 * Build URLSearchParams from all supported PipelineListFilters fields.
 * T020: forwards new sort/filter fields + sets If-None-Match via returned header.
 */
function _buildPipelineListParams(filters?: PipelineListFilters): URLSearchParams {
  const params = new URLSearchParams();

  // Status
  if (filters?.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    statuses.forEach((status) => {
      const statusLower = status.toLowerCase();
      if (statusLower === 'retired') {
        params.append('status', 'retired');
        params.append('include_retired', 'true');
        return;
      }
      const mappedStatus = STATUS_MAP[statusLower] || statusLower;
      params.append('status', mappedStatus);
      if (statusLower === 'cancelled') params.append('include_cancelled', 'true');
      if (statusLower === 'deleted') params.append('include_deleted', 'true');
    });
  } else {
    params.append('include_cancelled', 'true');
  }

  if (filters?.jira_key) params.append('jira_key', filters.jira_key);
  if (filters?.repo_name) params.append('repo_name', filters.repo_name);
  if (filters?.limit != null) params.append('limit', filters.limit.toString());
  if (filters?.offset != null) params.append('offset', filters.offset.toString());

  // T019/T020: extended sort + filter fields
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);
  if (filters?.team_id) params.append('team_id', filters.team_id);
  if (filters?.module_id) params.append('module_id', filters.module_id);
  if (filters?.issue_type) params.append('issue_type', filters.issue_type);
  if (filters?.work_state) params.append('work_state', filters.work_state);
  if (filters?.work_state === 'retired' || filters?.include_retired) {
    params.append('include_retired', 'true');
  }
  if (filters?.action_required_by) params.append('action_required_by', filters.action_required_by);
  if (filters?.view) params.append('view', filters.view);
  if (filters?.has_open_pr != null) params.append('has_open_pr', filters.has_open_pr.toString());
  if (filters?.failed_ci != null) params.append('failed_ci', filters.failed_ci.toString());
  if (filters?.unresolved_pr_comments != null) {
    params.append('unresolved_pr_comments', filters.unresolved_pr_comments.toString());
  }
  if (filters?.has_changes_since_last_seen != null) {
    params.append('has_changes_since_last_seen', filters.has_changes_since_last_seen.toString());
  }
  if (filters?.stale_days != null) params.append('stale_days', filters.stale_days.toString());
  if (filters?.requester) params.append('requester', filters.requester);
  if (filters?.assigned_developer) params.append('assigned_developer', filters.assigned_developer);
  if (filters?.current_stage) params.append('current_stage', filters.current_stage);
  if (filters?.created_after) params.append('created_after', filters.created_after);
  if (filters?.created_before) params.append('created_before', filters.created_before);
  if (filters?.last_action_after) params.append('last_action_after', filters.last_action_after);
  if (filters?.last_action_before) params.append('last_action_before', filters.last_action_before);
  if (filters?.since) params.append('since', filters.since);
  if (filters?.q) params.append('q', filters.q);

  return params;
}

/**
 * List pipelines with optional filtering.
 * T020: Sends If-None-Match on repeat polls; returns cached result on HTTP 304.
 */
export async function listPipelines(
  filters?: PipelineListFilters
): Promise<ListPipelinesResponse> {
  const params = _buildPipelineListParams(filters);
  const qs = params.toString();
  const endpoint = `/api/pipelines${qs ? `?${qs}` : ''}`;
  const cacheKey = _filterHash(filters);
  const cachedEtag = _etagCache.get(cacheKey);

  // Build extra headers for If-None-Match conditional request
  const extraHeaders: Record<string, string> = {};
  if (cachedEtag) extraHeaders['If-None-Match'] = cachedEtag;

  let url: string;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  const dashboardBaseUrl = getDashboardBaseUrl();
  if (dashboardBaseUrl) headers['X-Dashboard-Base-Url'] = dashboardBaseUrl;

  if (USE_PROXY) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    url = `${API_BASE_URL}/devagent/${cleanEndpoint}`;
    if (isAuthenticated()) {
      try {
        const token = await getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        console.warn('[DevAgent API] Failed to get access token:', e);
      }
    }
  } else {
    url = `${DIRECT_DEVAGENT_URL}${endpoint}`;
    if (DIRECT_API_KEY) headers['X-Api-Key'] = DIRECT_API_KEY;
    else console.warn('[DevAgent API] No API key set for direct DevAgent connection');
  }

  let response: Response;
  try {
    response = await fetch(url, { method: 'GET', headers });
  } catch (error) {
    throw normalizeNetworkError(error);
  }

  if (response.status === 304) {
    const cached = _listResponseCache.get(cacheKey);
    if (cached) return cached;
    throw new DevAgentApiError('Not Modified', 304, { not_modified: true });
  }

  if (!response.ok) {
    const errorData = await readErrorPayload(response);
    throw buildApiError(errorData, response.status);
  }

  // Cache the new ETag for future conditional requests
  const newEtag = response.headers.get('ETag');
  if (newEtag) _etagCache.set(cacheKey, newEtag);

  const data = await response.json() as ListPipelinesResponse;
  _listResponseCache.set(cacheKey, data);
  return data;
}

/**
 * Get pipeline detail by ID
 */
export async function getPipeline(pipelineId: string): Promise<PipelineDetail> {
  return devagentFetch<PipelineDetail>(`/pipelines/${pipelineId}`);
}

// ---------------------------------------------------------------------------
// T021 – GET /pipelines/facets
// ---------------------------------------------------------------------------

/**
 * Fetch aggregated facet counts for the pipeline list.
 * Accepts the same filter params as listPipelines so counts respect active filters.
 */
export async function getPipelineFacets(
  filters?: PipelineListFilters
): Promise<PipelineFacets> {
  const params = _buildPipelineListParams(filters);
  params.delete('limit');
  params.delete('offset');
  params.delete('sort_by');
  params.delete('sort_order');
  params.delete('since');
  const qs = params.toString();
  return devagentFetch<PipelineFacets>(`/api/pipelines/facets${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// T022 – Pin / Unpin / Mark-seen
// ---------------------------------------------------------------------------

/**
 * Pin a pipeline for the current user.
 * Backend: POST /api/pipelines/pins
 */
export async function pinPipeline(pipelineId: string): Promise<void> {
  await devagentFetch<{ ok: boolean; is_pinned: boolean }>(
    '/api/pipelines/pins',
    {
      method: 'POST',
      body: JSON.stringify({ pipeline_id: pipelineId }),
    }
  );
}

/**
 * Unpin a pipeline for the current user.
 * Backend: DELETE /api/pipelines/pins/{pipeline_id}
 */
export async function unpinPipeline(pipelineId: string): Promise<void> {
  await devagentFetch<{ ok: boolean; is_pinned: boolean }>(
    `/api/pipelines/pins/${pipelineId}`,
    { method: 'DELETE' }
  );
}

/**
 * Mark one or more pipelines as seen for the current user.
 * Pass null pipelineIds to mark ALL visible pipelines as seen.
 * Backend: POST /api/pipelines/mark-seen
 */
export async function markPipelinesSeen(pipelineIds?: string[]): Promise<void> {
  await devagentFetch<{ ok: boolean }>(
    '/api/pipelines/mark-seen',
    {
      method: 'POST',
      body: JSON.stringify({ pipeline_ids: pipelineIds ?? null }),
    }
  );
}

export async function getCapabilities(): Promise<DevAgentCapabilitiesResponse> {
  return devagentFetch<DevAgentCapabilitiesResponse>('/capabilities');
}

export interface AnalyticsFilters {
  start_date?: string;
  end_date?: string;
  workflow_type?: string;
  team_id?: string;
  module_id?: string;
  user_id?: string;
}

export interface AnalyticsMetricItem {
  id?: string;
  label: string;
  count: number;
  identity_type?: string;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
}

export interface AnalyticsFunnelStep {
  stage: string;
  label: string;
  count: number;
  conversion_from_start: number;
  conversion_from_previous: number;
  dropoff_from_previous: number;
}

export interface AnalyticsActivityPoint {
  date: string;
  active_users: number;
  completed: number;
  failed: number;
}

export interface DevAgentAnalyticsDashboard {
  generated_at: string;
  identity_mode?: 'hash' | 'email' | string;
  filters: AnalyticsFilters;
  filter_options?: {
    teams: AnalyticsMetricItem[];
    modules: AnalyticsMetricItem[];
    users: AnalyticsMetricItem[];
  };
  summary: {
    total_events: number;
    active_users: number;
    total_pipelines: number;
    created_pipelines: number;
    completed_pipelines: number;
    failed_pipelines: number;
    success_rate: number;
    retry_rate: number;
    total_cost_usd: number;
    avg_cost_usd: number;
  };
  event_breakdown?: {
    total_events: number;
    top_events: AnalyticsMetricItem[];
  };
  activity_over_time?: AnalyticsActivityPoint[];
  adoption: {
    dau: number;
    wau: number;
    mau: number;
    new_users: number;
    returning_users: number;
    by_team: AnalyticsMetricItem[];
    by_module: AnalyticsMetricItem[];
    by_user: AnalyticsMetricItem[];
  };
  effectiveness: {
    success_rate: number;
    retry_rate: number;
    avg_duration_seconds: number;
    total_cost_usd: number;
    avg_cost_usd: number;
    failure_stages: AnalyticsMetricItem[];
    retry_stages: AnalyticsMetricItem[];
  };
  llm_usage?: {
    total_cost_usd: number;
    avg_cost_usd: number;
    pipelines_with_cost: number;
    cost_by_status: AnalyticsMetricItem[];
  };
  funnel: AnalyticsFunnelStep[];
  feature_usage: {
    total_action_events: number;
    top_actions: AnalyticsMetricItem[];
    top_pages: AnalyticsMetricItem[];
  };
}

export interface FrontendAnalyticsEvent {
  event: string;
  distinct_id?: string;
  properties?: Record<string, string | number | boolean | null>;
  timestamp?: string;
}

function buildAnalyticsQuery(filters?: AnalyticsFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getDevAgentAnalyticsDashboard(
  filters?: AnalyticsFilters,
): Promise<DevAgentAnalyticsDashboard> {
  return devagentFetch<DevAgentAnalyticsDashboard>(`/api/analytics/dashboard${buildAnalyticsQuery(filters)}`);
}

export async function trackAnalyticsEvent(event: FrontendAnalyticsEvent): Promise<void> {
  await devagentFetch<{ ok: boolean }>('/api/analytics/events', {
    method: 'POST',
    body: JSON.stringify(event),
  });
}

export async function getRepoSelectionAccuracy(
  range: AnalyticsDateRange | AnalyticsTimeRange,
): Promise<RepoSelectionAccuracyResponse> {
  const query = typeof range === 'number'
    ? `?days=${range}`
    : buildAnalyticsQuery(range);
  return devagentFetch<RepoSelectionAccuracyResponse>(`/api/analytics/repo-selection/accuracy${query}`);
}

export async function getRepoOverSelection(
  range: AnalyticsDateRange | AnalyticsTimeRange,
): Promise<RepoOverSelectionResponse> {
  const query = typeof range === 'number'
    ? `?days=${range}`
    : buildAnalyticsQuery(range);
  return devagentFetch<RepoOverSelectionResponse>(`/api/analytics/repo-selection/over-selection${query}`);
}

export async function exportRepoSelectionCsv(startDate: string, endDate: string): Promise<Blob> {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  let url: string;
  const headers: Record<string, string> = {};

  if (USE_PROXY) {
    url = `${API_BASE_URL}/devagent/api/analytics/repo-selection/export?${params.toString()}`;
    if (isAuthenticated()) {
      try {
        const token = await getAccessToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.warn('[DevAgent API] Failed to get access token:', error);
      }
    }
  } else {
    url = `${DIRECT_DEVAGENT_URL}/api/analytics/repo-selection/export?${params.toString()}`;
    if (DIRECT_API_KEY) {
      headers['X-Api-Key'] = DIRECT_API_KEY;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (error) {
    throw normalizeNetworkError(error);
  }

  if (!response.ok) {
    throw buildApiError(await readErrorPayload(response), response.status);
  }
  if (response.headers.get('X-Export-Empty') === 'true') {
    throw new DevAgentApiError('NO_DATA_IN_RANGE', response.status, { empty: true });
  }
  return response.blob();
}

export async function getPipelineDocumentMetadata(pipelineId: string): Promise<DocumentSetResponse | null> {
  try {
    return await devagentFetch<DocumentSetResponse>(`/pipelines/${pipelineId}/documents/metadata`);
  } catch (error) {
    if (error instanceof DevAgentApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function listPipelineDocuments(pipelineId: string): Promise<DocumentListResponse | null> {
  try {
    return await devagentFetch<DocumentListResponse>(`/pipelines/${pipelineId}/documents`);
  } catch (error) {
    if (error instanceof DevAgentApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getPipelineDocumentDrafts(
  pipelineId: string
): Promise<{ generation_id: string | null; documents: Record<string, string> }> {
  return devagentFetch(`/pipelines/${pipelineId}/documents/drafts`);
}

export async function getDocumentQualityValidation(
  pipelineId: string, documentName: string
): Promise<DocumentQualityValidationStatus> {
  const params = new URLSearchParams({ document_name: documentName });
  return devagentFetch<DocumentQualityValidationStatus>(
    `/pipelines/${pipelineId}/documents/quality-validation?${params.toString()}`
  );
}

export async function startDocumentQualityValidation(
  pipelineId: string, documentName: string, expectedEtag?: string | null
): Promise<{ job_id: string; status: string; read_only: boolean }> {
  return devagentFetch(`/pipelines/${pipelineId}/documents/quality-validation`, {
    method: 'POST',
    body: JSON.stringify({ document_name: documentName, expected_etag: expectedEtag || null }),
  });
}

export async function getPipelineDocumentContent(
  pipelineId: string,
  documentName: string
): Promise<DocumentContentResponse | null> {
  const params = new URLSearchParams({ document_name: documentName });
  try {
    return await devagentFetch<DocumentContentResponse>(
      `/pipelines/${pipelineId}/documents/content?${params.toString()}`
    );
  } catch (error) {
    if (error instanceof DevAgentApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function exportPipelineDocuments(pipelineId: string): Promise<Blob> {
  const endpoint = `/pipelines/${pipelineId}/documents/export`;
  let url: string;
  const headers: Record<string, string> = {};

  if (USE_PROXY) {
    url = `${API_BASE_URL}/devagent/${endpoint.slice(1)}`;
    if (isAuthenticated()) {
      try {
        headers.Authorization = `Bearer ${await getAccessToken()}`;
      } catch (error) {
        console.warn('[DevAgent API] Failed to get access token:', error);
      }
    }
  } else {
    url = `${DIRECT_DEVAGENT_URL}${endpoint}`;
    if (DIRECT_API_KEY) {
      headers['X-Api-Key'] = DIRECT_API_KEY;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (error) {
    throw normalizeNetworkError(error);
  }
  if (!response.ok) {
    throw buildApiError(await readErrorPayload(response), response.status);
  }
  return response.blob();
}

export async function approvePipelineDocuments(
  pipelineId: string,
  request: ApproveDocumentPackageRequest
): Promise<ApproveDocumentPackageResponse> {
  return devagentFetch<ApproveDocumentPackageResponse>(
    `/pipelines/${pipelineId}/documents/approve`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export async function removePipelineRepository(
  pipelineId: string,
  request: RemoveRepositoryRequest
): Promise<RemoveRepositoryResponse> {
  return devagentFetch<RemoveRepositoryResponse>(
    `/pipelines/${pipelineId}/repositories/remove`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export async function retryRepositoryCorrection(pipelineId: string, eventId: string): Promise<{ effect_status: string }> {
  return devagentFetch(`/pipelines/${pipelineId}/repository-corrections/${eventId}/retry`, { method: 'POST' });
}

export async function changePipelineRepositories(
  pipelineId: string,
  request: ChangeRepositoriesRequest
): Promise<ChangeRepositoriesResponse> {
  return devagentFetch<ChangeRepositoriesResponse>(
    `/pipelines/${pipelineId}/repositories/change`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Create a new pipeline
 * 
 * @param request - Pipeline creation request
 * @param options - Optional parameters
 * @param options.force - Force create even if pipeline exists
 * @param options.branch_action - How to handle existing branch: "recreate" or "reuse"
 * @throws Error with conflict details if branch or pipeline already exists
 */
export async function createPipeline(
  request: CreatePipelineRequest,
  options?: {
    force?: boolean;
    branch_action?: 'recreate' | 'reuse';
    signal?: AbortSignal;
  }
): Promise<CreatePipelineResponse> {
  const params = new URLSearchParams();
  if (options?.force) {
    params.append('force', 'true');
  }
  if (options?.branch_action) {
    params.append('branch_action', options.branch_action);
  }
  
  const queryString = params.toString();
  const endpoint = `/pipelines${queryString ? `?${queryString}` : ''}`;
  
  // Build URL and headers based on proxy vs direct mode
  let url: string;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const dashboardBaseUrl = getDashboardBaseUrl();
  if (dashboardBaseUrl) {
    headers['X-Dashboard-Base-Url'] = dashboardBaseUrl;
  }
  
  if (USE_PROXY) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    url = `${API_BASE_URL}/devagent/${cleanEndpoint}`;
    if (isAuthenticated()) {
      try {
        const token = await getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch { /* proceed without token */ }
    }
  } else {
    url = `${DIRECT_DEVAGENT_URL}${endpoint}`;
    if (DIRECT_API_KEY) {
      headers['X-Api-Key'] = DIRECT_API_KEY;
    }
  }
  
  const timeoutController = new AbortController();
  let requestTimedOut = false;
  const timeoutId = globalThis.setTimeout(() => {
    requestTimedOut = true;
    timeoutController.abort();
  }, PIPELINE_CREATE_TIMEOUT_MS);
  const callerSignal = options?.signal;
  const abortFromCaller = () => timeoutController.abort();
  if (callerSignal?.aborted) {
    timeoutController.abort();
  } else {
    callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: timeoutController.signal,
    });
  } catch (error) {
    if (requestTimedOut) {
      throw new DevAgentApiError(
        'DevAgent did not complete pipeline creation within 165 seconds. Check the pipeline list before retrying.',
        504,
        { error: 'pipeline_create_timeout', timeout_ms: PIPELINE_CREATE_TIMEOUT_MS },
      );
    }
    if (callerSignal?.aborted) {
      throw new DevAgentApiError(
        'Pipeline creation request was cancelled.',
        499,
        { error: 'pipeline_create_cancelled' },
      );
    }
    throw normalizeNetworkError(error);
  } finally {
    globalThis.clearTimeout(timeoutId);
    callerSignal?.removeEventListener('abort', abortFromCaller);
  }

  if (!response.ok) {
    const errorData = await readErrorPayload(response);
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? (errorData as DevAgentErrorPayload).detail
        : errorData;

    // Return conflict data as a special error for the UI to handle
    if (response.status === 409) {
      const error = buildApiError(detail, response.status) as DevAgentApiError & {
        conflictData?: typeof detail;
      };
      error.conflictData = detail;
      throw error;
    }

    throw buildApiError(detail, response.status);
  }

  return response.json();
}

/**
 * Approve a pipeline stage
 */
export async function approvePipelineStage(
  pipelineId: string,
  stage: string,
  request?: ApproveStageRequest
): Promise<ApproveStageResponse> {
  return devagentFetch<ApproveStageResponse>(
    `/pipelines/${pipelineId}/approve/${stage}`,
    {
      method: 'POST',
      body: JSON.stringify(request || {}),  // ⭐ CRITICAL: Backend expects a body (even if empty)
    }
  );
}

export async function assignDeveloper(
  pipelineId: string,
  request: AssignDeveloperRequest
): Promise<AssignDeveloperResponse> {
  return devagentFetch<AssignDeveloperResponse>(
    `/pipelines/${pipelineId}/assign-developer`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export async function startImplementation(
  pipelineId: string,
  request: StartImplementationRequest = {}
): Promise<StartImplementationResponse> {
  return devagentFetch<StartImplementationResponse>(
    `/pipelines/${pipelineId}/start-implementation`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export interface RepositorySourceTree {
  repository: string;
  commit_sha: string;
  path: string;
  entries: { name: string; path: string; type: 'file' | 'directory' }[];
}

export interface RepositorySourceFile {
  repository: string;
  commit_sha: string;
  path: string;
  content: string;
  truncated: false;
}

export async function getRepositorySource(
  pipelineId: string,
  taskId: number,
  options: { path?: string; kind: 'tree' | 'file' },
): Promise<RepositorySourceTree | RepositorySourceFile> {
  const params = new URLSearchParams({ kind: options.kind, path: options.path || '' });
  return devagentFetch<RepositorySourceTree | RepositorySourceFile>(
    `/pipelines/${pipelineId}/repositories/${taskId}/source?${params.toString()}`,
  );
}

export async function getRepositoryWorkspace(
  pipelineId: string,
  repositoryId: number,
): Promise<RepositoryWorkspaceResponse> {
  return devagentFetch<RepositoryWorkspaceResponse>(
    `/pipelines/${pipelineId}/repositories/${repositoryId}/workspace`,
  );
}

export async function getRepositoryCheckpointDiff(
  pipelineId: string,
  repositoryId: number,
  checkpointId?: string,
): Promise<string> {
  const params = checkpointId
    ? `?${new URLSearchParams({ checkpoint_id: checkpointId }).toString()}`
    : '';
  return devagentFetch<string>(
    `/pipelines/${pipelineId}/repositories/${repositoryId}/diff${params}`,
  );
}

export async function getRepositoryCheckpointLogs(
  pipelineId: string,
  repositoryId: number,
  checkpointId?: string,
): Promise<string> {
  const params = checkpointId
    ? `?${new URLSearchParams({ checkpoint_id: checkpointId }).toString()}`
    : '';
  return devagentFetch<string>(
    `/pipelines/${pipelineId}/repositories/${repositoryId}/logs${params}`,
  );
}

export type PrGuardianChatMode = 'ask';

export interface PipelineAskAttachment { filename: string; content_base64: string }

export interface PipelineAskRequest {
  question: string;
  model?: string;
  user_requested_latest?: boolean;
  conversation_id?: string;
  package_version?: number;
  package_commit_sha?: string;
  document_name?: string;
  replay_message_id?: string;
  investigation_attempt_id?: string;
  investigation_report_sha256?: string;
  intent?: 'auto' | 'question';
  attachments?: PipelineAskAttachment[];
}

export type PipelineAskActivityPhase = 'queued' | 'loading_context' | 'starting_cursor' | 'working'
  | 'generating_answer' | 'saving_answer' | 'completed' | 'error' | 'cancelled' | 'tool' | 'commentary' | 'thinking';

export type PipelineAskToolSummary = 'Read file' | 'Search source' | 'Find files' | 'List files' | 'Check NuGet versions'
  | 'Evidence tool' | 'Tool operation' | 'Find related repositories' | 'Open reference repository'
  | 'Write file' | 'Edit file' | 'Run command';
export interface PipelineAskActivity {
  phase: PipelineAskActivityPhase;
  at: string;
  event_id?: string;
  tool_summary?: PipelineAskToolSummary;
  /** Public MCP namespace/tool label; never raw arguments or transport payloads. */
  tool_label?: string;
  /** Allowlisted executable name only; never a raw command line. */
  command_name?: string;
  status?: 'running' | 'completed' | 'error';
  duration_ms?: number;
  text?: string;
  path?: string;
  query?: string;
  result_summary?: string;
  started_at?: string;
  ended_at?: string;
}

export interface PipelineAskResponse {
  activity?: PipelineAskActivity;
  activity_history?: PipelineAskActivity[];
  activity_history_truncated?: boolean;
  investigation_binding?: { attempt_id: string; report_sha256: string; snapshot_sha256: string; report_bytes_sha256: string; evidence_sha256: string };
  answer: string;
  citations: Array<Record<string, unknown>>;
  source_coverage: Array<Record<string, unknown>>;
  selected_revisions: Record<string, string>;
  execution_handoff: boolean;
  conversation_id?: string;
  message_id?: string;
}

export type PipelineAskEvent =
  | { type: 'activity'; activity: PipelineAskActivity }
  | ({ type: 'context' } & Pick<PipelineAskResponse, 'citations' | 'source_coverage' | 'selected_revisions' | 'investigation_binding'>)
  | { type: 'delta'; text: string }
  | ({ type: 'snapshot'; queue_reason?: string } & Partial<PipelineAskResponse>)
  | ({ type: 'done' } & PipelineAskResponse)
  | { type: 'error'; message: string };

/** Progressive JSON snapshots remain incremental through the buffering Functions proxy. */
export async function pollPipelineAsk(
  pipelineId: string, request: PipelineAskRequest,
  onEvent: (event: PipelineAskEvent) => void, signal?: AbortSignal,
): Promise<PipelineAskResponse> {
  const endpoint = `/pipelines/${encodeURIComponent(pipelineId)}/ask/operations`;
  const accepted = await devagentFetch<{ operation_id: string }>(endpoint, {
    method: 'POST', body: JSON.stringify(request), signal,
  });
  let cursor = 0;
  let completed = false;
  try {
    // The durable server operation owns execution deadlines and lease recovery.
    while (!signal?.aborted) {
      const update = await devagentFetch<{ events: PipelineAskEvent[]; cursor: number; finished: boolean }>(
        `${endpoint}/${encodeURIComponent(accepted.operation_id)}?after=${cursor}`, { signal },
      );
      cursor = update.cursor;
      for (const event of update.events) {
        onEvent(event.type === 'error' ? { ...event, message: userFacingError(event.message, 503) } : event);
        if (event.type === 'error') throw new DevAgentApiError(userFacingError(event.message, 503), 503, event);
        if (event.type === 'done') { completed = true; return event; }
      }
      if (update.finished) throw new DevAgentApiError('Ask ended before completion. Refresh history before retrying.', 502);
      await new Promise<void>((resolve, reject) => {
        const aborted = () => { globalThis.clearTimeout(timer); reject(new DOMException('Cancelled', 'AbortError')); };
        const timer = globalThis.setTimeout(() => { signal?.removeEventListener('abort', aborted); resolve(); }, 300);
        signal?.addEventListener('abort', aborted, { once: true });
        if (signal?.aborted) { signal.removeEventListener('abort', aborted); aborted(); }
      });
    }
    throw new DevAgentApiError('Ask was cancelled. Your question is preserved.', 0);
  } finally {
    if (!completed) await devagentFetch(`${endpoint}/${encodeURIComponent(accepted.operation_id)}`, { method: 'DELETE' }).catch(() => undefined);
  }
}

export function progressivePipelineAsk(
  pipelineId: string, request: PipelineAskRequest,
  onEvent: (event: PipelineAskEvent) => void, signal?: AbortSignal,
): Promise<PipelineAskResponse> {
  return USE_PROXY ? pollPipelineAsk(pipelineId, request, onEvent, signal) : streamPipelineAsk(pipelineId, request, onEvent, signal);
}

/** Read authenticated SSE incrementally. Reconnect uses a persisted message ID, never reruns Ask. */
export async function streamPipelineAsk(
  pipelineId: string, request: PipelineAskRequest,
  onEvent: (event: PipelineAskEvent) => void, signal?: AbortSignal,
): Promise<PipelineAskResponse> {
  const controller = new AbortController();
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  const abort = () => { controller.abort(); void reader?.cancel().catch(() => undefined); };
  if (signal?.aborted) abort();
  signal?.addEventListener('abort', abort, { once: true });
  let connectionTimedOut = false;
  let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
  const renewConnection = () => {
    globalThis.clearTimeout(timeout);
    // The server sends heartbeats every 15 seconds, even while Cursor is quiet.
    timeout = globalThis.setTimeout(() => { connectionTimedOut = true; abort(); }, 60_000);
  };
  const checkConnection = () => {
    if (connectionTimedOut) throw new DevAgentApiError('Connection to DevAgent stopped responding. Your question is preserved; refresh history before retrying.', 502);
    if (controller.signal.aborted) throw new DOMException('Ask was cancelled', 'AbortError');
  };
  try {
    const response = await devagentFetch<Response>(`/pipelines/${encodeURIComponent(pipelineId)}/ask/stream`, {
      method: 'POST', body: JSON.stringify(request), signal: controller.signal,
      headers: { Accept: 'text/event-stream' },
    }, true);
    if (!response.body || !response.headers.get('Content-Type')?.includes('text/event-stream')) {
      throw new DevAgentApiError('Ask streaming is unavailable on this connection.', 502);
    }
    reader = response.body.getReader();
    renewConnection();
    const decoder = new TextDecoder();
    let pending = '';
    let receivedBytes = 0;
    while (true) {
      checkConnection();
      const { value, done } = await reader.read();
      checkConnection();
      if (value?.byteLength) renewConnection();
      if (value) receivedBytes += value.byteLength;
      // Delta text plus the authoritative final result can each contain escaped Unicode.
      if (receivedBytes > 4_096_000) throw new DevAgentApiError('Ask exceeded its response limit.', 502);
      pending = (pending + decoder.decode(value, { stream: !done })).replace(/\r\n/g, '\n');
      let boundary: number;
      while ((boundary = pending.indexOf('\n\n')) >= 0) {
        const frame = pending.slice(0, boundary);
        if (frame.length > 1_024_000) throw new DevAgentApiError('Ask exceeded its event limit.', 502);
        pending = pending.slice(boundary + 2);
        const data = frame.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
        if (!data) continue;
        const event = JSON.parse(data) as PipelineAskEvent;
        if (!['activity', 'context', 'delta', 'snapshot', 'done', 'error'].includes(event.type)) continue;
        if (event.type === 'delta' && typeof event.text !== 'string') throw new DevAgentApiError('Invalid Ask stream.', 502);
        onEvent(event.type === 'error' ? { ...event, message: userFacingError(event.message, 503) } : event);
        if (event.type === 'error') throw new DevAgentApiError(userFacingError(event.message, 503), 503, event);
        if (event.type === 'done') return event;
      }
      if (pending.length > 1_024_000) throw new DevAgentApiError('Ask exceeded its event limit.', 502);
      if (done) throw new DevAgentApiError('Ask disconnected before completion. Your question is preserved; refresh history before retrying.', 502);
    }
  } finally {
    globalThis.clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
    await reader?.cancel().catch(() => undefined);
    reader?.releaseLock();
  }
}

export interface PrGuardianAskContext {
  provider: string;
  repository_id?: string;
  repository_url?: string;
  repository_name?: string;
  pr_id: string;
  pr_url?: string;
  selected_comment_id?: string;
  selected_check_id?: string;
  selected_policy_id?: string;
  selected_repair_attempt_id?: string;
}

export interface PrGuardianAskRequest {
  message_type: 'question';
  context_type: 'pr_guardian';
  content: string;
  runner_model?: string;
  guardian_context: PrGuardianAskContext;
}

export async function askPipeline(
  pipelineId: string,
  request: PipelineAskRequest,
): Promise<PipelineAskResponse> {
  return devagentFetch(`/pipelines/${pipelineId}/ask`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getPipelineAskHistory(pipelineId: string): Promise<{ messages: Array<Record<string, unknown>> }> {
  return devagentFetch(`/pipelines/${pipelineId}/ask/history`);
}

export async function getDocumentEditStatus(pipelineId: string): Promise<DocumentEditStatus> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/documents/edit-reservation`);
}

export async function acquireDocumentEdit(pipelineId: string, request: {
  base_version?: number | null; base_etag?: string | null; evidence_sha256?: string | null;
  document_name: string; operation_id: string;
}): Promise<DocumentEditStatus> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/documents/edit-reservation`, {
    method: 'POST', body: JSON.stringify(request),
  });
}

export async function heartbeatDocumentEdit(pipelineId: string, reservation: DocumentEditReservation): Promise<DocumentEditStatus> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/documents/edit-reservation/heartbeat`, {
    method: 'POST', body: JSON.stringify({ reservation_id: reservation.reservation_id, fencing_token: reservation.fencing_token }),
  });
}

export async function releaseDocumentEdit(pipelineId: string, reservation: DocumentEditReservation): Promise<unknown> {
  const params = new URLSearchParams({ reservation_id: reservation.reservation_id, fencing_token: reservation.fencing_token || '' });
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/documents/edit-reservation?${params}`, { method: 'DELETE' });
}

export async function getBranchPreparation(pipelineId: string): Promise<BranchPreparationStatus> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/documents/branch-preparation`);
}

export async function submitDocumentEdit(pipelineId: string, reservation: DocumentEditReservation, request: {
  operation_kind: 'feedback' | 'artifact_regeneration'; message?: string; document_name: string; refresh_inputs?: boolean;
}): Promise<RegenerateArtifactResponse> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/documents/edit-reservation/submit`, {
    method: 'POST', body: JSON.stringify({ ...request, reservation_id: reservation.reservation_id, fencing_token: reservation.fencing_token }),
  });
}

export async function getPipelineAskCitation(
  pipelineId: string,
  citationId: string,
): Promise<Record<string, unknown>> {
  return devagentFetch(`/pipelines/${pipelineId}/ask/citations/${encodeURIComponent(citationId)}`);
}

export async function sendRepositoryImplementationMessage(
  pipelineId: string,
  repositoryId: number,
  request: {
    message_type: 'question' | 'change_request' | 'scope_change';
    runner_model?: string;
    content: string;
    context_type?: 'implementation' | 'document' | 'pr_guardian';
    document_name?: string;
    guardian_context?: PrGuardianAskContext;
  },
): Promise<{ status: string; delivery: string }> {
  return devagentFetch(
    `/pipelines/${pipelineId}/repositories/${repositoryId}/messages`,
    { method: 'POST', body: JSON.stringify(request) },
  );
}

export async function retryRepositoryImplementation(
  pipelineId: string,
  repositoryId: number,
): Promise<{ status: string }> {
  return devagentFetch(
    `/pipelines/${pipelineId}/repositories/${repositoryId}/retry-implementation`,
    { method: 'POST' },
  );
}

export async function retryAllFailedRepositoryImplementations(
  pipelineId: string,
): Promise<{ status: string; count: number }> {
  return devagentFetch(
    `/pipelines/${pipelineId}/repositories/retry-all-failed`,
    { method: 'POST' },
  );
}

export async function cancelRepositoryOrchestration(
  pipelineId: string,
): Promise<{ status: string; cancelled_job_ids: string[]; errors: string[] }> {
  return devagentFetch(
    `/pipelines/${pipelineId}/repositories/cancel-orchestration`,
    { method: 'POST' },
  );
}

export async function confirmManualImplementation(
  pipelineId: string
): Promise<ConfirmManualImplementationResponse> {
  return devagentFetch<ConfirmManualImplementationResponse>(
    `/pipelines/${pipelineId}/confirm-manual-implementation`,
    {
      method: 'POST',
    }
  );
}

export async function resolveRequirementGap(
  pipelineId: string,
  request: ResolveRequirementGapRequest,
): Promise<ResolveRequirementGapResponse> {
  return devagentFetch<ResolveRequirementGapResponse>(
    `/pipelines/${pipelineId}/resolve-requirement-gap`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Check PR merge status for a pipeline
 */
export interface PRStatusResponse {
  success: boolean;
  is_merged: boolean;
  status: 'active' | 'completed' | 'abandoned' | null;
  merge_status?: string;
  title?: string;
  pr_url?: string;
  pr_id?: number;
  error?: string;
}

export async function getPRStatus(pipelineId: string): Promise<PRStatusResponse> {
  return devagentFetch<PRStatusResponse>(`/pipelines/${pipelineId}/pr-status`);
}

/**
 * Retry a failed pipeline
 */
export async function retryPipeline(
  pipelineId: string,
  prOnly: boolean = false,
  testOnly: boolean = false,
  runnerType?: string,
  runnerModel?: string,
  engineeringAuthoringWorkflow?: 'cursor_agent_v1'
): Promise<RetryPipelineResponse> {
  const body: Record<string, string | boolean> = {
    pr_only: prOnly,
    test_only: testOnly,
  };
  if (engineeringAuthoringWorkflow) body.engineering_authoring_workflow = engineeringAuthoringWorkflow;
  if (runnerType && !prOnly) {
    body.runner_type = runnerType;
  }
  if (runnerModel && !prOnly) {
    body.runner_model = runnerModel;
  }

  return devagentFetch<RetryPipelineResponse>(`/pipelines/${pipelineId}/retry`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function recoverDocumentWorkflow(pipelineId: string, expectedUpdatedAt: string) {
  return devagentFetch(`/pipelines/${pipelineId}/recover-document-workflow`, {
    method: 'POST', body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }),
  });
}

export interface RegenerateArtifactRequest {
  reason?: string;
  preserve_feedback?: boolean;
  refresh_inputs?: boolean;
  document_name?: string;
  scope?: 'document' | 'package';
  expected_etag?: string;
}

export interface RegenerateArtifactResponse {
  pipeline_id: string;
  stage: 'spec' | 'testplan' | 'plan' | 'tasks' | 'analyze' | 'document-package';
  status: string;
  message: string;
  operation_id?: string;
}

export async function regenerateArtifact(
  pipelineId: string,
  stage: 'spec' | 'testplan' | 'plan' | 'tasks' | 'analyze' | 'document-package',
  request: RegenerateArtifactRequest
): Promise<RegenerateArtifactResponse> {
  return devagentFetch<RegenerateArtifactResponse>(
    `/pipelines/${pipelineId}/regenerate/${stage}`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export async function getDefectInvestigation(
  pipelineId: string
): Promise<DefectInvestigationResponse> {
  return devagentFetch<DefectInvestigationResponse>(
    `/pipelines/${pipelineId}/defect-investigation`
  );
}

export async function createDefectInvestigationAttempt(
  pipelineId: string,
  userFeedback?: string,
  refreshInputs = false
): Promise<DefectInvestigationResponse> {
  return devagentFetch<DefectInvestigationResponse>(
    `/pipelines/${pipelineId}/defect-investigation/attempts`,
    {
      method: 'POST',
      body: JSON.stringify({
        user_feedback: userFeedback || null,
        user_direction: userFeedback || null,
        refresh_inputs: refreshInputs,
      }),
    }
  );
}

export async function createDefectReviewDecision(
  pipelineId: string,
  payload: {
    attempt_id: string;
    workspace_approval?: boolean;
    document_sha256?: string;
    etag?: string | null;
    confirmed_dependents?: string[];
    decision_status: ReviewDecisionStatus;
    user_feedback?: string;
    decision_rationale?: string;
    is_override?: boolean;
    refresh_inputs?: boolean;
    refresh_instructions?: boolean;
  }
) {
  return devagentFetch(`/pipelines/${pipelineId}/defect-investigation/review-decisions`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function submitDefectInvestigationRating(
  pipelineId: string,
  payload: {
    attempt_id: string;
    stars: number;
    feedback?: string;
  }
): Promise<DefectInvestigationResponse> {
  return devagentFetch<DefectInvestigationResponse>(
    `/pipelines/${pipelineId}/defect-investigation/ratings`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

/**
 * List test logs for a pipeline
 */
export async function listTestLogs(
  pipelineId: string
): Promise<{ pipeline_id: string; logs: TestLogEntry[] }> {
  return devagentFetch<{ pipeline_id: string; logs: TestLogEntry[] }>(
    `/pipelines/${pipelineId}/test-logs`
  );
}

export interface ContainerLogStream {
  container_name: string;
  content: string;
}

export interface ContainerJobLogsResponse {
  pipeline_id: string;
  container_id: string;
  source: string;
  available: boolean;
  omitted_older_actions?: number;
  actions?: PipelineRecordedAction[];
  fetched_at: string;
  streams: ContainerLogStream[];
}

export interface PipelineRecordedAction {
  sequence: number;
  timestamp: string;
  label: string;
  invocation_id?: string;
  /** Sanitized public activity only; never model reasoning or raw runner payloads. */
  activity?: {
    phase: 'commentary';
    event_id: string;
    text: string;
    at: string;
  } | {
    phase: 'starting_cursor' | 'thinking' | 'working' | 'completed' | 'error';
    at: string;
  };
  operation?: Omit<PipelineAskActivity, 'phase' | 'tool_summary'> & {
    phase: 'tool';
    command_name?: string;
    tool_summary: PipelineAskToolSummary | 'Write file' | 'Edit file' | 'Run command' | 'Find related repositories' | 'Open reference repository';
  };
}

/** Persisted pipeline jobs, including document authoring before repository checkpoints exist. */
export interface PipelineActivityJob {
  job_id: string;
  job_type: string;
  stage_name?: string | null;
  status: string;
  attempt?: number;
  document_name?: string | null;
  package_version?: number | null;
  review_state?: 'pending' | 'running' | 'completed' | 'unavailable' | null;
  review_error?: string | null;
  completion_summary?: { text: string; source: 'cursor' | 'published_investigation' | 'quality_review' | 'job_result' } | null;
  execution_name?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  heartbeat_at?: string | null;
  updated_at?: string | null;
  runner_type?: string | null;
  runner_model?: string | null;
  error_message?: string | null;
  execution_progress?: { phase?: string; dispatch_started_at?: string; runner_started_at?: string };
  logs_available: boolean;
}

export function getPipelineActivity(pipelineId: string): Promise<{ pipeline_id: string; jobs: PipelineActivityJob[] }> {
  return devagentFetch(`/pipelines/${pipelineId}/activity`);
}

export function getPipelineActivityLogs(pipelineId: string, jobId: string): Promise<ContainerJobLogsResponse> {
  return devagentFetch(`/pipelines/${pipelineId}/activity/${encodeURIComponent(jobId)}/logs`);
}

/** Get bounded, server-redacted stdout/stderr for a pipeline container job. */
export async function getContainerJobLogs(
  pipelineId: string,
  containerId: string,
  tail: number = 400
): Promise<ContainerJobLogsResponse> {
  return devagentFetch<ContainerJobLogsResponse>(
    `/pipelines/${pipelineId}/jobs/${encodeURIComponent(containerId)}/logs?tail=${tail}`
  );
}

/**
 * Get structured test execution status for a pipeline
 */
export async function getTestStatus(
  pipelineId: string
): Promise<PipelineTestStatusResponse> {
  return devagentFetch<PipelineTestStatusResponse>(`/pipelines/${pipelineId}/test-status`);
}

/**
 * Get a specific test log content
 */
export async function getTestLog(
  pipelineId: string,
  logName: string
): Promise<string> {
  let url: string;
  const headers: Record<string, string> = {};

  if (USE_PROXY) {
    // Production: Proxy through Azure Functions (same auth as devagentFetch)
    url = `${API_BASE_URL}/devagent/pipelines/${pipelineId}/test-logs/${logName}`;

    // Include Azure AD Bearer token for proxy authentication
    if (isAuthenticated()) {
      try {
        const token = await getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        console.warn('[DevAgent API] Failed to get access token for test log:', e);
      }
    }
  } else {
    // Local development: Direct connection to DevAgent
    url = `${DIRECT_DEVAGENT_URL}/pipelines/${pipelineId}/test-logs/${logName}`;
    if (DIRECT_API_KEY) {
      headers['X-Api-Key'] = DIRECT_API_KEY;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (error) {
    throw normalizeNetworkError(error);
  }
  if (!response.ok) {
    throw buildApiError(await readErrorPayload(response), response.status);
  }
  return response.text();
}

/**
 * Cancel a running pipeline
 */
export async function cancelPipeline(
  pipelineId: string
): Promise<CancelPipelineResponse> {
  return devagentFetch<CancelPipelineResponse>(
    `/api/pipelines/${pipelineId}/cancel`,
    {
      method: 'POST',
    }
  );
}

/**
 * Delete a pipeline permanently
 * 
 * @param pipelineId - The pipeline to delete
 * @param options - Optional parameters
 * @param options.delete_branch - If true, also delete the associated git branch
 */
export async function deletePipeline(
  pipelineId: string,
  options?: {
    delete_branch?: boolean;
    cancel_running?: boolean;
    cleanup_worktree?: boolean;
    delete_local_branch?: boolean;
    delete_remote_branch?: boolean;
    abandon_pr?: boolean;
  }
): Promise<CancelPipelineResponse & { cleanup_results?: Record<string, unknown> }> {
  const params = new URLSearchParams();
  if (options?.delete_branch) {
    params.append('delete_branch', 'true');
  }
  if (typeof options?.cancel_running === 'boolean') {
    params.append('cancel_running', String(options.cancel_running));
  }
  if (typeof options?.cleanup_worktree === 'boolean') {
    params.append('cleanup_worktree', String(options.cleanup_worktree));
  }
  if (typeof options?.delete_local_branch === 'boolean') {
    params.append('delete_local_branch', String(options.delete_local_branch));
  }
  if (typeof options?.delete_remote_branch === 'boolean') {
    params.append('delete_remote_branch', String(options.delete_remote_branch));
  }
  if (typeof options?.abandon_pr === 'boolean') {
    params.append('abandon_pr', String(options.abandon_pr));
  }
  
  const queryString = params.toString();
  const url = `/api/pipelines/${pipelineId}${queryString ? `?${queryString}` : ''}`;
  
  return devagentFetch<CancelPipelineResponse & { cleanup_results?: Record<string, unknown> }>(
    url,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Get dashboard statistics
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  return devagentFetch<DashboardStats>('/api/stats');
}

/**
 * Stream pipeline logs via Server-Sent Events
 * Returns an EventSource for real-time log streaming
 * 
 * Note: SSE (EventSource) doesn't support custom headers, so this uses
 * direct connection in dev mode. In production, the proxy route handles auth.
 */
export function streamPipelineLogs(pipelineId: string): EventSource {
  const endpoint = `api/pipelines/${pipelineId}/logs/stream`;
  const url = USE_PROXY
    ? `${API_BASE_URL}/devagent/${endpoint}`
    : `${DIRECT_DEVAGENT_URL}/${endpoint}`;
  return new EventSource(url);
}

/**
 * PR Comment Thread types
 */
export interface PRCommentThread {
  id: number;
  status: string;
  is_resolved: boolean;
  file_path: string | null;
  line_start: number | null;
  line_end: number | null;
  is_inline: boolean;
  pipeline_repository_id?: number | null;
  pr_url?: string;
  pr_id?: number | string;
  repo_name?: string;
  comments: {
    id: number;
    author: string;
    author_id: string;
    content: string;
    published_date: string;
    last_updated_date: string;
    comment_type: string;
  }[];
  last_updated: string;
}

export interface PRCommentsResponse {
  success: boolean;
  threads: PRCommentThread[];
  total_count: number;
  pr_url?: string;
  pr_id?: number;
  repo_name?: string;
  error?: string;
}

/**
 * Get all PR comment threads for a pipeline
 */
export async function getPRComments(
  pipelineId: string,
  pipelineRepositoryId?: number | null,
): Promise<PRCommentsResponse> {
  const query = pipelineRepositoryId == null
    ? ''
    : `?pipeline_repository_id=${encodeURIComponent(String(pipelineRepositoryId))}`;
  return devagentFetch<PRCommentsResponse>(`/pipelines/${pipelineId}/pr-comments${query}`);
}

/**
 * Handle a PR comment (fix, reply, or ignore)
 */
export interface PRCommentActionRequest {
  action: 'reply' | 'ignore';
  reply_content?: string;
  pipeline_repository_id?: number | null;
}

export interface PRCommentActionResponse {
  success: boolean;
  action: string;
  message: string;
  thread_id?: number;
}

export async function handlePRComment(
  pipelineId: string,
  threadId: number,
  request: PRCommentActionRequest
): Promise<PRCommentActionResponse> {
  return devagentFetch<PRCommentActionResponse>(
    `/pipelines/${pipelineId}/pr-comments/${threadId}/action`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export interface FixAllUnresolvedResponse {
  monitor?: Partial<PrMonitorStatus>;
  success: boolean;
  message: string;
  status?: string;
  container_id?: string;
  implementation_runner_type?: string;
  implementation_runner_model?: string | null;
}

export interface FixAllUnresolvedRequest {
  runner_type?: string;
  runner_model?: string;
  runner_selection_source?: 'pr_fix_selector';
  pipeline_repository_id?: number;
}

export async function fixAllUnresolvedComments(
  pipelineId: string,
  request: FixAllUnresolvedRequest = {}
): Promise<FixAllUnresolvedResponse> {
  return devagentFetch<FixAllUnresolvedResponse>(
    `/pipelines/${pipelineId}/fix-all-unresolved`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export type PrMonitorState =
  | 'waiting_for_review'
  | 'findings_detected'
  | 'repair_running'
  | 'waiting_for_checks'
  | 'ready_for_approval'
  | 'approved'
  | 'blocked'
  | 'paused'
  | 'stopped';

export interface PrMonitorStatus {
  monitor_id: string;
  pipeline_id: string;
  platform: 'azure_devops' | 'github' | 'gitlab';
  repository_id: string;
  pr_id: string;
  pr_url: string;
  source_branch?: string | null;
  target_branch?: string | null;
  source_sha?: string | null;
  state: PrMonitorState;
  is_active: boolean;
  runner_type: string;
  runner_model?: string | null;
  cycle_count: number;
  repair_count: number;
  no_progress_count: number;
  consecutive_error_count: number;
  last_event_at?: string | null;
  last_reconciled_at?: string | null;
  settle_until?: string | null;
  next_poll_at?: string | null;
  active_stage_id?: string | null;
  active_container_id?: string | null;
  container_status?: string | null;
  repair_phase?: 'provisioning' | 'running' | string | null;
  findings: Array<{
    kind: string;
    id: string;
    name: string;
    detail?: string;
  }>;
  checks: {
    merge_status?: string;
    pending_required_checks?: Array<Record<string, unknown>>;
    running_builds?: Array<Record<string, unknown>>;
    failed_required_checks?: Array<Record<string, unknown>>;
    sonar?: Record<string, unknown> | null;
    sonar_configuration_message?: string | null;
  };
  reviewers: {
    satisfied?: boolean;
    required_count?: number;
    approved_count?: number;
    rejected_count?: number;
  };
  blocked_reason?: string | null;
  last_error?: string | null;
  started_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
}

export interface PrMonitorControlRequest {
  reason?: string;
  runner_type?: string;
  runner_model?: string;
  pipeline_repository_id?: number;
}

export async function getPrMonitor(pipelineId: string): Promise<PrMonitorStatus> {
  return devagentFetch<PrMonitorStatus>(`/pipelines/${pipelineId}/pr-monitor`);
}

export async function pausePrMonitor(
  pipelineId: string,
  request: PrMonitorControlRequest = {}
): Promise<PrMonitorStatus> {
  return devagentFetch<PrMonitorStatus>(`/pipelines/${pipelineId}/pr-monitor/pause`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function resumePrMonitor(
  pipelineId: string,
  request: PrMonitorControlRequest = {}
): Promise<PrMonitorStatus> {
  return devagentFetch<PrMonitorStatus>(`/pipelines/${pipelineId}/pr-monitor/resume`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updatePrMonitorRunner(
  pipelineId: string,
  request: PrMonitorControlRequest = {}
): Promise<PrMonitorStatus> {
  return devagentFetch<PrMonitorStatus>(`/pipelines/${pipelineId}/pr-monitor/runner`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function reconcilePrMonitor(pipelineId: string): Promise<PrMonitorStatus> {
  return devagentFetch<PrMonitorStatus>(`/pipelines/${pipelineId}/pr-monitor/reconcile`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function refreshPrMonitor(
  pipelineId: string,
  request: PrMonitorControlRequest = {}
): Promise<PrMonitorStatus> {
  return devagentFetch<PrMonitorStatus>(`/pipelines/${pipelineId}/pr-monitor/refresh`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export interface RepairActivityStep {
  id: string;
  kind: string;
  title: string;
  status: string;
  detail?: string;
  links?: Record<string, unknown>;
  started_at?: string | null;
  finished_at?: string | null;
  source?: string;
}

export interface RepairActivityLive {
  pipeline_id: string;
  stage_id?: string;
  current_step_id?: string;
  updated_at?: string;
  steps: RepairActivityStep[];
  sonar?: Record<string, unknown> | null;
}

export interface CursorTranscriptLive {
  last_activity_at?: string;
  activity_state?: 'running' | 'waiting' | 'tool_running' | 'recovering';
  idle_seconds?: number;
  idle_timeout_seconds?: number;
  active_tool_count?: number;
  resume_attempt?: number;
  repair_stage_id?: string;
  previous_invocation_id?: string;
  message?: string;
  pipeline_id: string;
  invocation_id: string;
  stage_id?: string;
  stage_name?: string;
  mode?: string;
  output_path?: string;
  status: string;
  started_at?: string;
  updated_at?: string;
  batch_index?: number | null;
  event_count?: number;
  bytes?: number;
  invocations?: Array<{
    invocation_id: string;
    bytes?: number;
    updated_at?: string;
    event_count?: number;
  }>;
}

export interface CursorRunMetadata extends CursorTranscriptLive {
  job_id?: string;
  pipeline_repository_id?: string | number | null;
  completed_at?: string;
  error_message?: string;
  document_names?: string[];
  outputs?: Array<{ name: string; sha256: string; size_bytes: number }>;
  changed_files?: string[];
  validation?: unknown;
  diff?: string;
  diff_truncated?: boolean;
  diff_stat?: string;
  base_sha?: string;
  head_sha?: string;
  transcript_available?: boolean;
}

export function getCursorRunHistory(pipelineId: string): Promise<{ invocations: CursorRunMetadata[]; warning?: string | null }> {
  return devagentFetch(`/pipelines/${pipelineId}/cursor-transcript`);
}

export function getCursorRunMetadata(pipelineId: string, invocationId: string): Promise<CursorRunMetadata> {
  return devagentFetch(`/pipelines/${pipelineId}/cursor-transcript/${encodeURIComponent(invocationId)}/metadata`);
}

export interface CursorChatEvent {
  seq: number;
  kind: string;
  subtype?: string;
  ts?: string;
  invocation_id: string;
  batch_index?: number | null;
  summary?: string;
  detail?: string;
  payload?: string;
  truncated?: boolean;
}

export interface CursorTranscriptEvents {
  pipeline_id: string;
  invocation_id: string;
  events: CursorChatEvent[];
  next_seq: number;
  eof: boolean;
  total_lines?: number;
  status?: string;
  available?: boolean;
}

export async function getRepairActivityLive(
  pipelineId: string,
  pipelineRepositoryId?: number | null,
): Promise<RepairActivityLive> {
  const params = new URLSearchParams();
  if (pipelineRepositoryId != null) {
    params.set('pipeline_repository_id', String(pipelineRepositoryId));
  }
  const query = params.toString();
  return devagentFetch<RepairActivityLive>(
    `/pipelines/${pipelineId}/repair-activity/live${query ? `?${query}` : ''}`,
  );
}

export async function getCursorTranscriptLive(pipelineId: string, pipelineRepositoryId?: number | null): Promise<CursorTranscriptLive> {
  return devagentFetch<CursorTranscriptLive>(`/pipelines/${pipelineId}/cursor-transcript/live${pipelineRepositoryId == null ? "" : `?pipeline_repository_id=${pipelineRepositoryId}`}`);
}

export async function getCursorTranscriptEvents(
  pipelineId: string,
  invocationId: string,
  sinceSeq: number = 0,
  limit: number = 200
): Promise<CursorTranscriptEvents> {
  const params = new URLSearchParams({
    since_seq: String(sinceSeq),
    limit: String(limit),
  });
  return devagentFetch<CursorTranscriptEvents>(
    `/pipelines/${pipelineId}/cursor-transcript/${encodeURIComponent(invocationId)}?${params}`
  );
}

/**
 * Pending Changes types
 */
export interface PendingFixFile {
  path: string;
  diff: string;
  has_changes: boolean;
}

export interface PendingFix {
  thread_id: number;
  summary: string;
  files: PendingFixFile[];
  timestamp: string;
  is_ci_fix?: boolean;
  file_path?: string;
  guardrail_status?: 'passed' | 'blocked' | 'pending';
  guardrail_policy?: Record<string, unknown>;
  guardrail_changed_files?: string[];
}

export interface FailedFix {
  thread_id: number;
  file_path?: string;
  error: string;
  timestamp: string;
  guardrail_status?: 'blocked' | 'passed';
  blocked_reason?: string;
  needs_human_review?: boolean;
  changed_files?: string[];
  guardrail_policy?: Record<string, unknown>;
}

export interface PendingChangesResponse {
  success: boolean;
  has_pending_changes: boolean;
  fixes: PendingFix[];
  failed_fixes?: FailedFix[];
  total_files_modified: number;
  git_status?: { status: string; path: string }[];
  repo_root?: string;
  pr_id?: number;
  repo_name?: string;
  error?: string;
}

/**
 * Get pending (uncommitted) changes from PR comment fixes
 */
export async function getPendingChanges(pipelineId: string): Promise<PendingChangesResponse> {
  return devagentFetch<PendingChangesResponse>(`/pipelines/${pipelineId}/pending-changes`);
}

/**
 * Commit all pending PR comment fixes
 */
export interface CommitFixesRequest {
  skip_tests?: boolean;
}

export interface CommitFixesResponse {
  success: boolean;
  message: string;
  status?: string;
  step?: string;
  fixes_committed?: number;
  branch?: string;
  error?: string;
  tests_failed?: boolean;
  test_output?: string;
  test_command?: string;
  guardrail_status?: 'blocked' | 'passed' | 'pending';
  blocked_reason?: string;
  needs_human_review?: boolean;
}

export async function commitPendingFixes(
  pipelineId: string, 
  request: CommitFixesRequest = {}
): Promise<CommitFixesResponse> {
  return devagentFetch<CommitFixesResponse>(
    `/pipelines/${pipelineId}/commit-fixes`,
    { 
      method: 'POST',
      body: JSON.stringify(request)
    }
  );
}

/**
 * Poll the status of an async commit-fixes task
 */
export interface CommitStatusResponse {
  state: 'idle' | 'starting' | 'installing_deps' | 'running_tests' | 'fixing_build' | 'committing' | 'replying' | 'completed' | 'failed';
  step?: string;
  started_at?: string;
  success?: boolean;
  message?: string;
  fixes_committed?: number;
  branch?: string;
  error?: string;
  tests_failed?: boolean;
  test_output?: string;
  test_command?: string;
  guardrail_status?: 'blocked' | 'passed' | 'pending';
  blocked_reason?: string;
  needs_human_review?: boolean;
}

export async function getCommitStatus(pipelineId: string): Promise<CommitStatusResponse> {
  return devagentFetch<CommitStatusResponse>(`/pipelines/${pipelineId}/commit-status`);
}

/**
 * Poll the status of single-pass PR fix task
 */
export interface FixStatusResponse {
  state: 'idle' | 'running' | 'completed' | 'failed';
  step?: string;
  threads_resolved?: number[];
  threads_failed?: Array<{ thread_id: number; reason?: string; error?: string }>;
  files_modified?: string[];
  error?: string;
}

export async function getFixStatus(pipelineId: string): Promise<FixStatusResponse> {
  return devagentFetch<FixStatusResponse>(`/pipelines/${pipelineId}/fix-status`);
}

/**
 * Discard all pending PR comment fixes
 */
export interface DiscardFixesResponse {
  success: boolean;
  message: string;
  fixes_discarded?: number;
  error?: string;
}

export async function discardPendingFixes(pipelineId: string): Promise<DiscardFixesResponse> {
  return devagentFetch<DiscardFixesResponse>(
    `/pipelines/${pipelineId}/discard-fixes`,
    { method: 'POST' }
  );
}

/**
 * CI/Build Failure types
 */
export interface TestFailure {
  test_name: string;
  test_class: string;
  full_name: string;
  outcome: string;
  error_message: string;
  stack_trace: string;
  duration_ms: number;
  run_name: string;
  build_name?: string;
}

export interface BuildFailure {
  build_name: string;
  build_url: string;
  build_id: number | null;
  description: string;
  is_required?: boolean;
  test_failures: TestFailure[];
}

export interface PrCheckItem {
  name: string;
  state: string;
  description?: string;
  url?: string;
  is_required?: boolean;
  source?: string;
  test_failures?: TestFailure[];
  build_id?: number | null;
}

export interface CIFailuresResponse {
  success: boolean;
  has_failures: boolean;
  has_blocking_failures?: boolean;
  has_optional_failures?: boolean;
  failed_builds?: string[];
  failures?: BuildFailure[];
  blocking_failures?: BuildFailure[];
  optional_failures?: BuildFailure[];
  required_checks?: PrCheckItem[];
  optional_checks?: PrCheckItem[];
  all_required_checks_succeeded?: boolean;
  optional_checks_failed_count?: number;
  total_test_failures?: number;
  pr_url?: string;
  repo_name?: string;
  pr_id?: number;
  sonar_cloud?: Record<string, unknown> | null;
  error?: string;
}

/**
 * Get CI/build failures for a pipeline's PR
 */
export async function getCIFailures(
  pipelineId: string,
  pipelineRepositoryId?: number,
): Promise<CIFailuresResponse> {
  const query = pipelineRepositoryId == null
    ? ''
    : `?pipeline_repository_id=${encodeURIComponent(String(pipelineRepositoryId))}`;
  return devagentFetch<CIFailuresResponse>(`/pipelines/${pipelineId}/ci-failures${query}`);
}

/**
 * Fix CI/test failures with AI
 */
export interface CIFixRequest {
  pipeline_repository_id?: number;
  fix_all?: boolean;
  failure_indices?: number[];
  runner_type?: string;
  runner_model?: string;
}

export interface CIFixResponse {
  success: boolean;
  message: string;
  failures_to_fix?: number;
  pipeline_repository_id?: number;
  job_id?: string;
  error?: string;
}

export async function fixCIFailures(
  pipelineId: string,
  request: CIFixRequest = { fix_all: true }
): Promise<CIFixResponse> {
  return devagentFetch<CIFixResponse>(
    `/pipelines/${pipelineId}/fix-ci-failures`,
    {
      method: 'POST',
      body: JSON.stringify(request)
    }
  );
}

// ============================================================================
// Artifact & Clarification APIs
// ============================================================================

/**
 * Get artifact content and any sync warning for a pipeline stage.
 *
 * Returns a response with `status: "syncing"` (and no content) when the
 * backend has started restoring the pipeline worktree from the remote branch
 * (HTTP 202).  The caller should poll until the status is absent/undefined.
 */
export async function getArtifactContent(
  pipelineId: string,
  artifactType: string,
  options: { includeStale?: boolean; stageId?: string } = {},
): Promise<ArtifactContentResponse> {
  // Use raw fetch so we can inspect the 202 status before the helper throws.
  let url: string;
  const headers: Record<string, string> = {};

  const query = new URLSearchParams();
  if (options.includeStale) query.set('include_stale', 'true');
  if (options.stageId) query.set('stage_id', options.stageId);
  const querySuffix = query.size > 0 ? `?${query.toString()}` : '';

  if (USE_PROXY) {
    const cleanEndpoint = `api/pipelines/${pipelineId}/artifacts/${artifactType}${querySuffix}`;
    url = `${API_BASE_URL}/devagent/${cleanEndpoint}`;
    if (isAuthenticated()) {
      try {
        const token = await getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        console.warn('[DevAgent API] Failed to get access token:', e);
      }
    }
  } else {
    url = `${DIRECT_DEVAGENT_URL}/api/pipelines/${pipelineId}/artifacts/${artifactType}${querySuffix}`;
    if (DIRECT_API_KEY) {
      headers['X-Api-Key'] = DIRECT_API_KEY;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (error) {
    throw normalizeNetworkError(error);
  }

  // 202 means the backend is restoring the worktree — tell the UI to show a
  // "syncing" message and retry after a few seconds.
  if (response.status === 202) {
    const body = (await response.json().catch(() => ({}))) as Record<string, string>;
    return {
      content: '',
      sync_warning: null,
      status: 'syncing',
      syncing_message: body.message ?? 'Restoring pipeline workspace from the remote branch…',
    };
  }

  if (!response.ok) {
    throw buildApiError(await readErrorPayload(response), response.status);
  }

  const bodyText = await response.text();
  const syncWarning = response.headers.get('X-Artifact-Sync-Warning');
  const artifactRevision = response.headers.get('X-Artifact-Revision');
  const reviewedArtifactDigest = response.headers.get('X-Reviewed-Artifact-Digest');

  try {
    const parsed = JSON.parse(bodyText) as Partial<ArtifactContentResponse> | null;
    if (parsed && typeof parsed.content === 'string') {
      const parsedArtifactRevision = parsed.artifact_revision ?? artifactRevision;
      const parsedReviewedArtifactDigest = parsed.reviewed_artifact_digest ?? reviewedArtifactDigest;
      return {
        content: parsed.content,
        sync_warning: parsed.sync_warning ?? syncWarning,
        ...(parsedArtifactRevision ? { artifact_revision: parsedArtifactRevision } : {}),
        ...(parsedReviewedArtifactDigest ? { reviewed_artifact_digest: parsedReviewedArtifactDigest } : {}),
      };
    }
  } catch {
    // Backward-compatible fallback for plain-text artifact responses.
  }

  return {
    content: bodyText,
    sync_warning: syncWarning,
    ...(artifactRevision ? { artifact_revision: artifactRevision } : {}),
    ...(reviewedArtifactDigest ? { reviewed_artifact_digest: reviewedArtifactDigest } : {}),
  };
}

export interface ArtifactContentResponse {
  content: string;
  sync_warning: string | null;
  artifact_revision?: string | null;
  reviewed_artifact_digest?: string | null;
  /** Set to "syncing" when the backend is restoring the worktree (HTTP 202). */
  status?: 'syncing';
  /** Human-readable message to display while syncing. */
  syncing_message?: string;
}

/**
 * Clarification types shared with DevAgentArtifactViewer
 */
export interface ClarificationQuestion {
  id?: number;
  clarification_id?: number;
  question: string;
  context?: string;
  category?: string;
  recommended?: string;
  recommended_reasoning?: string;
  suggested_answer?: string;
  options?: (string | { letter: string; description: string })[];
  type?: string;
  answer?: string;
  requested_by?: string;
  requested_at?: string;
  /** Indicates whether this question has been answered. Derived from answered_at timestamp by backend. Used for approval validation. */
  is_answered?: boolean;
}

export interface ClarificationResponse {
  questions: ClarificationQuestion[];
  clarification_id: string;
  mode?: 'interactive' | 'freeform_only' | 'review_changes' | 'appended' | 'logged_only';
  message?: string;
  instructions?: string;
}

export interface StoredClarificationQuestionsResponse {
  success: boolean;
  questions: ClarificationQuestion[];
  has_unanswered: boolean;
  iteration?: number;
}

export interface FreeformResponse {
  success: boolean;
  clarification_id: number;
  mode: 'processing' | 'review_changes' | 'appended' | 'logged_only';
  state?: 'processing' | 'completed' | 'failed';
  reviewer_role?: string;
  message: string;
  answer: string;
  changes?: string[];
  original_content?: string;
  suggested_content?: string;
  artifact_path?: string;
  instructions?: string;
  poll_path?: string;
  error?: string;
  version?: number;
  etag?: string;
  review_gates?: Record<string, { status?: string; reviewerRole?: string; reviewedBy?: string | null; reviewedAt?: string | null; source?: string | null }>;
  base_document_sha256?: string;
  base_operation_id?: string | null;
}

export interface QAReviewResponse {
  success: boolean;
  clarification_id: number;
  mode: 'review_changes';
  message: string;
  changes?: string[];
  changes_made?: string[];
  original_content: string;
  suggested_content: string;
  artifact_path: string;
  git_persisted?: boolean;
  git_error?: string | null;
  instructions?: string;
  base_document_sha256?: string;
  base_operation_id?: string | null;
}

export interface QAProcessingResponse extends FreeformResponse {
  mode: 'processing';
  state: 'processing';
}

export interface QANoChangesResponse {
  success: boolean;
  mode: 'no_changes';
  message: string;
  changes_made?: string[];
  artifact_path?: string;
  git_persisted?: boolean;
  git_error?: string | null;
}

export interface ApplyChangesResponse {
  success: boolean;
  message: string;
  mode?: 'processing';
  state?: 'processing' | 'completed' | 'failed';
  clarification_id?: number;
  poll_path?: string;
  operation_id?: string;
  provisional_content?: string;
  provisional_sha256?: string;
  queue_position?: number | null;
  queue_total?: number | null;
  deduplicated?: boolean;
  answer?: string;
  artifact_path?: string;
  version?: number;
  etag?: string;
  review_gates?: Record<string, { status?: string; reviewerRole?: string; reviewedBy?: string | null; reviewedAt?: string | null; source?: string | null }>;
}

export interface DocumentPackageMutationStatusResponse extends ApplyChangesResponse {
  state: 'processing' | 'completed' | 'failed';
  operation_id: string;
  status?: 'queued' | 'running';
  dependent_operation_id?: string;
  persistence_state?: 'persisted';
  error?: string;
}

export interface AnalyzeRecommendationPreviewItem {
  stage: 'spec' | 'plan' | 'tasks';
  title: string;
  original_content: string;
  suggested_content: string;
  artifact_path: string;
  changes: string[];
  has_changes: boolean;
}

export interface AnalyzeRecommendationPreviewResponse {
  success: boolean;
  mode: 'review_changes';
  message: string;
  instructions?: string;
  items: AnalyzeRecommendationPreviewItem[];
}

export interface ApplyAnalyzeRecommendationsRequest {
  items: Array<{
    stage: 'spec' | 'plan' | 'tasks';
    artifact_path: string;
    suggested_content: string;
  }>;
}

export interface ApplyAnalyzeRecommendationsResponse {
  success: boolean;
  message: string;
  persist_message?: string;
  branch_name?: string;
  items_applied: number;
}

export interface RerunAnalyzeResponse {
  success: boolean;
  pipeline_id: string;
  stage: 'analyze';
  status: string;
  message: string;
}

export interface FreeformStatusResponse extends FreeformResponse {
  state: 'processing' | 'completed' | 'failed';
}

/**
 * Start interactive clarification for an artifact
 */
export async function startClarification(
  pipelineId: string,
  artifactType: string,
  options?: { document_name?: string }
): Promise<ClarificationResponse> {
  const params = new URLSearchParams();
  if (options?.document_name) params.set('document_name', options.document_name);
  const query = params.toString();
  return devagentFetch<ClarificationResponse>(
    `/pipelines/${pipelineId}/clarify/${artifactType}/interactive${query ? `?${query}` : ''}`,
    { method: 'POST' }
  );
}

export async function getStageQuestions(
  pipelineId: string,
  artifactType: string,
  options?: { document_name?: string }
): Promise<StoredClarificationQuestionsResponse> {
  const params = new URLSearchParams();
  if (options?.document_name) params.set('document_name', options.document_name);
  const query = params.toString();
  return devagentFetch<StoredClarificationQuestionsResponse>(
    `/pipelines/${pipelineId}/clarify/${artifactType}/questions${query ? `?${query}` : ''}`
  );
}

/**
 * Submit answers to clarification questions
 */
export async function submitClarificationAnswers(
  pipelineId: string,
  artifactType: string,
  data: {
    save_only?: boolean;
    answers: Record<number, string>;
    questions: ClarificationQuestion[];
    requester_email: string;
    requester_name?: string;
    document_name?: string;
    reservation_id?: string;
    fencing_token?: string;
  }
): Promise<QAReviewResponse | QAProcessingResponse | QANoChangesResponse> {
  return devagentFetch<QAReviewResponse | QAProcessingResponse | QANoChangesResponse>(
    `/pipelines/${pipelineId}/clarify/${artifactType}/submit-answers`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Submit freeform feedback on an artifact
 */
export async function submitFreeformFeedback(
  pipelineId: string,
  artifactType: string,
  data: {
    question: string;
    requester_email: string;
    requester_name?: string;
    document_name?: string;
    reservation_id?: string;
    fencing_token?: string;
  }
): Promise<FreeformResponse> {
  return devagentFetch<FreeformResponse>(
    `/pipelines/${pipelineId}/clarify/${artifactType}/submit-freeform`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Poll async status for freeform feedback processing.
 */
export async function getFreeformFeedbackStatus(
  pipelineId: string,
  artifactType: string,
  clarificationId: number
): Promise<FreeformStatusResponse> {
  return devagentFetch<FreeformStatusResponse>(
    `/pipelines/${pipelineId}/clarify/${artifactType}/status/${clarificationId}`
  );
}

/**
 * Apply suggested changes from clarification review
 */
export async function applyClarificationChanges(
  pipelineId: string,
  artifactType: string,
  data: {
    suggested_content: string;
    artifact_path: string;
    clarification_id: number | string;
    document_name?: string;
    reservation_id?: string;
    fencing_token?: string;
    expected_etag?: string;
    expected_document_sha256?: string;
    base_document_sha256?: string;
    base_operation_id?: string | null;
    workspace_approval?: boolean;
    confirmed_dependents?: string[];
    mark_reviewed?: boolean;
    async_review?: boolean;
    approve_after_review?: boolean;
    review_status?: 'reviewed' | 'rejected' | 'pending' | 'draft';
    requester_email?: string;
    requester_name?: string;
  }
): Promise<ApplyChangesResponse> {
  return devagentFetch<ApplyChangesResponse>(
    `/pipelines/${pipelineId}/clarify/${artifactType}/apply-changes`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

export async function getDocumentPackageMutationStatus(
  pipelineId: string,
  operationId: string,
): Promise<DocumentPackageMutationStatusResponse> {
  return devagentFetch<DocumentPackageMutationStatusResponse>(
    `/pipelines/${pipelineId}/clarify/document-package/mutations/${operationId}`,
  );
}

/**
 * Generate preview diffs for analyze-stage recommendations across spec/plan/tasks.
 */
export async function previewAnalyzeRecommendations(
  pipelineId: string
): Promise<AnalyzeRecommendationPreviewResponse> {
  return devagentFetch<AnalyzeRecommendationPreviewResponse>(
    `/pipelines/${pipelineId}/analyze/recommendations/preview`,
    { method: 'POST' }
  );
}

/**
 * Apply previewed analyze recommendations to spec/plan/tasks.
 */
export async function applyAnalyzeRecommendations(
  pipelineId: string,
  data: ApplyAnalyzeRecommendationsRequest
): Promise<ApplyAnalyzeRecommendationsResponse> {
  return devagentFetch<ApplyAnalyzeRecommendationsResponse>(
    `/pipelines/${pipelineId}/analyze/recommendations/apply`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Re-run analyze generation from the current approved inputs.
 */
export async function rerunAnalyze(
  pipelineId: string
): Promise<RerunAnalyzeResponse> {
  return devagentFetch<RerunAnalyzeResponse>(
    `/pipelines/${pipelineId}/analyze/rerun`,
    {
      method: 'POST',
    }
  );
}

export async function reassignPipelineOwner(
  pipelineId: string,
  payload: ReassignOwnershipPayload,
): Promise<ReassignOwnershipResponse> {
  return devagentFetch<ReassignOwnershipResponse>(
    `/api/pipelines/${pipelineId}/reassign`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function assignStageReviewer(
  pipelineId: string,
  payload: AssignStageReviewerPayload,
): Promise<AssignStageReviewerResponse> {
  return devagentFetch<AssignStageReviewerResponse>(
    `/api/pipelines/${pipelineId}/assign-reviewer`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}


export async function searchUsers(query: string, pipelineId?: string): Promise<UserSearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (pipelineId) {
    params.set('pipeline_id', pipelineId);
  }
  return devagentFetch<UserSearchResponse>(`/api/users/search?${params.toString()}`);
}

export interface GoldTestCases {
  schema_version?: 1 | 2;
  executable_count?: number;
  blocked_count?: number;
  cases?: unknown[];
  blocked_candidates?: unknown[];
  repository_catalogs?: Record<string, GoldTestCases>;
  accepted: boolean;
  generation_status?: string | null;
  active_document_name?: string | null;
  completed_document_names?: string[];
  package_revision?: number | null;
  target_repository?: string | null;
  error?: string | null;
  utm_markdown: string;
  rtm_json: Record<string, unknown>;
  lint_results: unknown[];
  confidence_score?: number | null;
  verdict?: string | null;
}

export function getGoldTestCases(pipelineId: string): Promise<GoldTestCases> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/gold-catalog`);
}

export function retryGoldTestCases(pipelineId: string): Promise<unknown> {
  return devagentFetch(`/pipelines/${encodeURIComponent(pipelineId)}/gold-catalog/retry`, { method: 'POST' });
}

export type DocumentDependencyReason = {
  reason: 'upstream_content_changed' | 'source_lineage_unknown';
  source_document: string;
  source_sha256: string;
  previous_source_sha256: string | null;
  dependent_origin_version: number | null;
};

export function getDocumentApprovalImpact(pipelineId: string, documentName: string) {
  return devagentFetch<{ document_name: string; document_sha256: string; etag: string; version: number; dependent_documents: string[]; dependent_document_reasons?: Record<string, DocumentDependencyReason> }>(
    `/pipelines/${encodeURIComponent(pipelineId)}/document-approval-impact?document_name=${encodeURIComponent(documentName)}`);
}

export type InvestigationApprovalImpact = { attempt_id: string; document_sha256: string; etag: string | null; dependent_documents: string[] };
export const getInvestigationApprovalImpact = (pipelineId: string) => devagentFetch<InvestigationApprovalImpact>(`/pipelines/${pipelineId}/defect-investigation/approval-impact`);
export const saveInvestigationQuestionAnswer = (pipelineId: string, payload: { attempt_id: string; document_sha256: string; answers: Record<string, string>; questions: ClarificationQuestion[] }) => devagentFetch(`/pipelines/${pipelineId}/defect-investigation/question-answers`, { method: 'POST', body: JSON.stringify(payload) });
